<template>
  <ResourceDialog
    @save="onSave"
    ref="dialog"
    :processing="processing"
    title="Assign"
  >
    <template #form="{ localValue }">
      <LabeledSimpleSelect
        v-if="allowTeam"
        v-model="localValue.assignee_type"
        @update:model-value="handleAssigneeTypeChange(localValue, $event)"
        :items="assigneeTypeOptions"
        item-title="text"
        item-value="value"
        message="Assignee type"
      />

      <LabeledAutocomplete
        v-if="localValue.assignee_type === 'Member'"
        v-model="localValue.assignee_id"
        @click:clear="emitSearch('')"
        @update:search="emitSearch"
        :hard-lock="unassign"
        :items="memberItems"
        :loading="memberSearchLoading"
        item-title="name"
        item-value="id"
        message="Member"
        variant="filled"
        clearable
      />

      <LabeledAutocomplete
        v-if="allowTeam && localValue.assignee_type === 'Team'"
        v-model="localValue.assignee_id"
        :hard-lock="unassign"
        :items="teamItems"
        item-title="name"
        item-value="id"
        message="Team"
        variant="filled"
        clearable
      />

      <LabeledSwitch
        v-if="hadAssigneeWhenOpened"
        v-model="unassign"
        title="Unassign"
      />
    </template>
  </ResourceDialog>
</template>

<script setup>
import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledSwitch from '@/shared/components/form/LabeledSwitch.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';

const { api } = useApi();

const props = defineProps({
  allowTeam: {
    type: Boolean,
    default: false,
  },
  memberItems: {
    type: Array,
    default: () => [],
  },
  memberSearchLoading: {
    type: Boolean,
    default: false,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  teamItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['save', 'search-members', 'update:memberItems']);

const assigneeTypeOptions = [
  { text: 'Specialist', value: 'Member' },
  { text: 'Team', value: 'Team' },
];

const dialog = ref(null);
const hadAssigneeWhenOpened = ref(false);
const unassign = ref(false);

defineExpose({ close, open });

function emitSearch(query) {
  emit('search-members', query);
}

function handleAssigneeTypeChange(localValue, newType) {
  localValue.assignee_type = newType;
  localValue.assignee_id = null;
}

function onSave(formData) {
  emit('save', { ...formData, _unassign: unassign.value });
}

async function open(initial = {}) {
  unassign.value = false;
  hadAssigneeWhenOpened.value = Boolean(initial.assignee_id);

  const initialValue = {
    assignee_type: initial.assignee_type || 'Member',
    assignee_id: initial.assignee_id || null,
  };

  if (
    initialValue.assignee_type === 'Member' &&
    initialValue.assignee_id &&
    !props.memberItems.find((member) => member.id === initialValue.assignee_id)
  ) {
    const response = await api.organization.member.get(initialValue.assignee_id);
    if (response?.data) {
      emit('update:memberItems', [response.data, ...props.memberItems]);
      await nextTick();
    }
  }

  await nextTick();
  dialog.value?.open(initialValue);
}

function close() {
  dialog.value?.close();
}
</script>
